import { EMPTY_OBJECT } from '@unifyapps/carbon/consts/empty';
import { useIsUserLoggedIn } from '@unifyapps/carbon/hooks/useIsUserLoggedIn';
import type { ThemeType } from '@unifyapps/defs/theme/types';
import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import { useFindEntitiesByIds } from '@unifyapps/network/generated/entity-rest-api/entity-rest-api';
import type { EntityBulkFetchRequest } from '@unifyapps/network/generated/models/entityBulkFetchRequest';
import { useGetQueryMeta } from '@unifyapps/network/hooks/useGetQueryMeta';
import _values from 'lodash/values';
import { useMemo } from 'react';
import { useGetAuthComponentBulkFetch } from './fetchAndInvalidation/deployedEntities/data/useAuthComponentBulkFetch';

/**
 * Fetches theme entities for a list of ids, before and after login.
 *
 * For public interfaces (queryMeta.isPublicInterface === true), themes are fetched via the
 * standard entity endpoint, which the network layer rewrites to the public route prefix —
 * no user session is required, and the auth-facing bulk fetch endpoint is skipped.
 *
 * For private interfaces, themes are loaded through the auth-facing endpoint before login
 * so the interface can render correctly without a user session. After login, the hook
 * switches to the standard entity endpoint to avoid unnecessary auth-service requests.
 *
 * Request bodies are built only from `themeIds`, so two mounts with the same ids share
 * one react-query entry (and one network call) on either transport. Pass a memoized
 * `themeIds` array so the request body isn't rebuilt on every render.
 */
export function useThemeEntitiesByIds(themeIds: string[]) {
  const isUserLoggedIn = useIsUserLoggedIn();
  const queryMeta = useGetQueryMeta();
  // public interfaces hit /api-endpoint/external/... via queryMeta.isPublicInterface; they
  // never need the auth bulk-fetch fallback. Logged-in users always use the standard endpoint.
  const shouldUseStandardEntityApi = isUserLoggedIn || Boolean(queryMeta?.isPublicInterface);
  const hasThemeIds = themeIds.length > 0;

  const entityBulkFetchRequest: EntityBulkFetchRequest = useMemo(() => {
    return {
      entities: [
        {
          entityType: UAEntityTypes.Theme,
          entityIdVersions: themeIds.map((themeId) => ({
            entityId: themeId,
            currentVersion: -1,
          })),
        },
      ],
    };
  }, [themeIds]);

  const {
    data: bulkFetchData,
    isLoading: isBulkFetchLoading,
    error: bulkFetchError,
  } = useGetAuthComponentBulkFetch(entityBulkFetchRequest, {
    query: {
      enabled: !shouldUseStandardEntityApi && hasThemeIds,
    },
  });

  const {
    data: themeSearchResponse,
    isLoading: isThemeSearchLoading,
    error: themeSearchError,
  } = useFindEntitiesByIds<{ objects: ThemeType[] }>(
    UAEntityTypes.Theme,
    { ids: themeIds },
    {
      query: {
        enabled: shouldUseStandardEntityApi && hasThemeIds,
      },
    },
  );

  const themes = useMemo(() => {
    const bulkFetchedThemes = _values(
      bulkFetchData?.entityTypeVsEntities?.[UAEntityTypes.Theme] ?? EMPTY_OBJECT,
    ) as ThemeType[];

    if (shouldUseStandardEntityApi) {
      return themeSearchResponse?.objects ?? bulkFetchedThemes;
    }
    return bulkFetchedThemes;
  }, [shouldUseStandardEntityApi, themeSearchResponse, bulkFetchData]);

  return {
    themes,
    isLoading: shouldUseStandardEntityApi ? isThemeSearchLoading : isBulkFetchLoading,
    error: shouldUseStandardEntityApi ? themeSearchError : bulkFetchError,
  };
}
